<template>
  <content-subpage
    :visible="visible"
    :titles="titles"
    class="announcement-edit-content-subpage"
    @content-subpage-hidden="actionHidden"
  >
    <template v-if="showHeaderButton" #header-buttons>
      <fetch-data-button
        :kind="dataSaveButton.kind"
        :size="dataSaveButton.size"
        :error="dataSaveButton.error"
        :loading="dataSaveButton.loading"
        :icon-only="dataSaveButton.iconOnly"
        :tip-position="dataSaveButton.tipPosition"
        :error-message="dataSaveButton.errorMessage"
        :completed="dataSaveButton.completed"
        @click="handleUpdate"
        @reset-error-state="handleSaveButtonResetError"
        @reset-completed-state="handleSaveButtonResetCompleted"
      >
        <svg-icon :name="dataSaveButton.iconName" kind="action" />
      </fetch-data-button>
    </template>
    <template #main-content>
      <div class="announcement-editor" :class="{ edit: isEdit }">
        <overlay-loader v-if="isLoading" />
        <template v-else>
          <div v-if="isEdit && hasEditPermission" class="announcement-actions">
            <overflow-menu :flip-menu="true">
              <template #trigger>
                <svg-icon name="Kebab24" kind="action" />
              </template>
              <OverflowMenuItem @click="handleDelete">
                <svg-icon name="Trash24" kind="action" />
                {{ t("button_delete") }}
              </OverflowMenuItem>
            </overflow-menu>
          </div>
          <div class="announcement-editor-content">
            <cv-form class="announcement" @submit.prevent>
              <div class="field-group sender-receiver">
                <dl class="field-set receiver">
                  <dt v-if="isEdit" class="field-label">
                    {{ t("announcement_receiver_receiver_selection_label") }}
                  </dt>
                  <dd v-if="isEdit" class="field-value">
                    <template v-if="hasIndividualReceiver && !isClassGroupNotification">
                      <span>{{ notificationIndividualReceiver }}</span>
                    </template>
                    <template
                      v-if="hasStudentIndividualNotification || hasTeacherIndividualNotification"
                    >
                      <template v-if="hasStudentIndividualNotification">
                        <label>{{ t("announcement_receiver_student_label") }}</label>
                        <span>{{ notificationStudentIndividuals }}</span>
                      </template>
                      <template v-if="hasTeacherIndividualNotification">
                        <label>{{ t("announcement_receiver_teacher_label") }}</label>
                        <span>{{ notificationTeacherIndividuals }}</span>
                      </template>
                    </template>

                    <template v-else-if="isClassGroupNotification">
                      <span>{{ t("announcement_receiver_all_student_teachers_label") }}</span>
                    </template>
                  </dd>
                  <template v-else>
                    <dd v-if="isGroupPreselected" class="field-value">
                      <label-component
                        kind="class-group"
                        :text="preselectGroup.groupName"
                        :subject-key="preselectGroup.subjectKey"
                      />
                    </dd>
                    <dd v-else class="field-input">
                      <multi-select
                        kind="white"
                        class="student-group-filter"
                        :label="t('announcement_select_class_group_community_form_label')"
                        :filterable="true"
                        :user-filter="true"
                        :auto-filter="true"
                        :options="filterGroups"
                        :value="formData.classGroupSelectedUuids"
                        selection-feedback="fixed"
                        @change="handleGroupChange"
                      >
                        <template v-if="error.group !== ''" #invalid-message>
                          {{ error.group }}
                        </template>
                      </multi-select>
                    </dd>
                  </template>
                </dl>
                <dl v-if="isEdit" class="field-set owner">
                  <dt class="field-label">
                    {{ t("announcement_owner_form_label") }}
                  </dt>
                  <dd class="field-value">
                    {{ notification.createdByName }}
                  </dd>
                </dl>
                <dl v-if="isEdit && isClassGroupNotification" class="field-set class-group-label">
                  <dt class="field-label">
                    {{ t("announcement_receiver_class_group_selection_label") }}
                  </dt>
                  <dd
                    v-for="(receiver, $index) in notificationReceiver"
                    :key="$index"
                    class="field-value"
                  >
                    <label-component
                      kind="class-group"
                      :text="receiver.name"
                      :subject-key="receiver.subjectKey"
                    />
                  </dd>
                </dl>
                <dl v-if="isEdit" class="field-set type-text school">
                  <dt class="field-label">{{ t("announcement_receiver_school_form_label") }}</dt>
                  <dd class="field-value">{{ schoolName }}</dd>
                </dl>
                <dl v-if="isEdit" class="field-set last-modified-by">
                  <dt class="field-label">
                    {{ t("announcement_last_modified_by_form_label") }}
                  </dt>
                  <dd class="field-value">
                    {{ notification.modifiedByName }}
                  </dd>
                </dl>
              </div>
              <dl v-if="!isEdit" class="field-set receiver">
                <dt class="field-label">
                  {{ t("announcement_receiver_receiver_selection_label") }}
                </dt>
                <dd class="field-input">
                  <dropdown-component
                    kind="white"
                    :value="recipientsSelected"
                    @change="handleChangeRecipientsSelected"
                  >
                    <cv-dropdown-item
                      v-for="(option, index) in studentRecipientSelection"
                      :key="index"
                      :value="option"
                    >
                      {{ option }}
                    </cv-dropdown-item>
                  </dropdown-component>
                </dd>
              </dl>
              <dl v-if="!isEdit" class="field-set type-button">
                <dd class="field-input">
                  <overflow-menu-button
                    :disabled="addRecipientButtonDisabled"
                    kind="tertiary"
                    size="field"
                    @click.prevent
                  >
                    <template #default>
                      {{ t("announcement_add_recipient_receiver_button_label") }}
                    </template>
                    <template #overflow-menu>
                      <OverflowMenuItem @click="handleAddStudent">
                        <svg-icon name="Plus24" kind="action" />
                        {{ t("announcement_add_student_receiver_button_label") }}
                      </OverflowMenuItem>
                      <OverflowMenuItem @click="handleAddTeacher">
                        <svg-icon name="Plus24" kind="action" />
                        {{ t("announcement_add_teacher_receiver_button_label") }}
                      </OverflowMenuItem>
                    </template>
                  </overflow-menu-button>
                </dd>
                <dd v-if="showStudentTags" class="field-value individual-recipients">
                  <span class="field-label">{{ t("announcement_students_receiver_label") }}</span>
                  <ul>
                    <li v-for="user in individualRecipients" :key="user.userId">
                      <user-tag
                        :user="user"
                        :show-detail-on-hover="true"
                        :show-detail-on-click="true"
                        :removable="true"
                        @remove="handleRemoveStudents"
                      >
                        <template #label>
                          <label-component kind="recipient" :text="user.username" />
                        </template>
                      </user-tag>
                    </li>
                  </ul>
                </dd>
                <dd v-if="showTeacherTags" class="field-value individual-recipients">
                  <span class="field-label">{{ t("announcement_teachers_receiver_label") }}</span>
                  <ul>
                    <li v-for="user in individualTeachers" :key="user.userId">
                      <user-tag
                        :user="user"
                        :show-detail-on-hover="true"
                        :show-detail-on-click="true"
                        :removable="true"
                        @remove="handleRemoveTeachers"
                      >
                        <template #label>
                          <label-component kind="recipient" :text="user.name" />
                        </template>
                      </user-tag>
                    </li>
                  </ul>
                </dd>
              </dl>
              <dl class="field-set title">
                <dt class="field-label">{{ t("announcement_title_form_label") }}</dt>
                <dd class="field-input">
                  <text-input
                    :value="formData.title"
                    :mandatory="true"
                    placeholder="Title"
                    :character-limit="titleCharacterLimit"
                    :error-message="error.title"
                    :text-id="dirtyId"
                    @input="handleTitleChange"
                  />
                </dd>
              </dl>
              <dl class="field-set message">
                <dt class="field-label">{{ t("message_form_label") }}</dt>
                <dd class="field-input">
                  <rich-text-editor
                    ref="richTextEditor"
                    kind="teacher_tinymce_4"
                    :initial-content="formData.content"
                    size="large"
                    :is-mandatory="true"
                    @change="handleMessageChange"
                  />
                </dd>
              </dl>
              <div class="field-group schedule">
                <!-- Start Date Time -->
                <dl class="field-set start-date">
                  <dd class="field-input">
                    <DatePicker
                      class="date-picker"
                      kind="single"
                      :date-label="t('announcement_start_date_form_label')"
                      :value="formData.startDate"
                      :cal-options="calendarOption"
                      :invalid-message="error.startDate"
                      placeholder="dd Mmm yyyy"
                      @change="onStartDateChange"
                    />
                    <text-input
                      class="time-picker"
                      :label="t('announcement_start_time_form_label')"
                      :input-type="'time'"
                      :value="formData.startTime"
                      :error-message="error.startTime"
                      :text-id="dirtyId"
                      @change="onUpdateStartTime"
                    />
                  </dd>
                </dl>
              </div>
            </cv-form>
          </div>
        </template>
      </div>
    </template>
    <template v-if="canSubmit" #action-button>
      <fetch-data-button
        :kind="dataSaveButton.kind"
        :error="dataSaveButton.error"
        :loading="dataSaveButton.loading"
        tip-position="top"
        tip-alignment="end"
        :error-message="dataSaveButton.errorMessage"
        @click="handleSend"
        @reset-error-state="handleSaveButtonResetError"
        @reset-completed-state="handleSaveButtonResetCompleted"
      >
        <template v-if="dataSaveButton.completed">
          {{ t("button_state_saved") }}
        </template>
        <template v-else-if="dataSaveButton.error">
          {{ t("button_state_error") }}
        </template>
        <template v-else>
          <svg-icon name="Plus24" kind="action" />
          {{ t("button_create") }}
        </template>
      </fetch-data-button>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { useVm } from "@utils/vm";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import {
  ContentSubpageParams,
  useContentSubpage,
} from "@plugins/content-subpage/composables/content-subpage.ts";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import UserTag from "@plugins/user-tag/components/UserTag.vue";
import { LOCKABLE_RESOURCE_TYPE } from "@plugins/common/service/lock.constant";
import NotificationEditContentSubpageMixin from "@modules/notification/mixin/notification.edit.content.subpage.mixin";
import NotificationUtils from "@modules/notification/service/notification.utils";
import LockService from "@plugins/common/service/lock.service";
import {
  ROLE_IN_GROUP,
  STUDENTS_IN_GROUP_SELECTED,
} from "@plugins/student-group/constant/student.group.constant";
import { isEmpty } from "@utils/var.check";
import AddRecipientsContentSubpage from "@modules/notification/components/AddRecipientsContentSubpage.vue";
import AddTeacherFromGroupContentSubpage from "@modules/notification/components/AddTeacherFromGroupContentSubpage.vue";
import { NOTIFICATION_AUDIENCE } from "@modules/notification/service/notification.constant";
import DOMUtils from "@utils/dom.utils";
import { NOTIFICATION_SCOPE } from "../service/notification.constant";

// defineOptions
defineOptions({
  mixins: [NotificationEditContentSubpageMixin],
});

// Props
interface NotificationEditContentSubpageParam extends ContentSubpageParams {
  kind?: string;
  data?: Record<string, unknown>;
}

interface Props {
  params?: NotificationEditContentSubpageParam | null;
}

const props = withDefaults(defineProps<Props>(), {
  params: null,
});

// Composables
const { t } = useI18n();
const { vm, vmExpose } = useVm();

const id = props.params.id;
const dirtyId = ref(id);

const toast = useToast();
const modal = useModal();

const contentSubpageInstance = useContentSubpageInstance();
const {
  titles,
  visible,
  dataSaveButton,
  handleSaveButtonResetError,
  handleSaveButtonResetCompleted,
  actionHidden,
  defaultExpose,
  setDataState,
  setErrorMessage,
  setDirty,
  removeDirty,
} = useContentSubpage(props.params);

// Data
const studentGroupData = ref(props.params?.data?.studentGroupData);
const preselectGroup = ref(props.params?.data?.preselectGroup);
const sortedGroups = ref<unknown[]>([]);
const selected = ref<unknown[]>([]);
const individualRecipients = ref<unknown[]>([]);
const individualTeachers = ref<unknown[]>([]);
//const availableStudents = ref<unknown[]>([]);
const recipientsSelected = ref("");

// Watchers

// Computed
const isGroupPreselected = computed(() => props.params?.data?.preselectGroup);

const showHeaderButton = computed(() => vm.kind === "editor" && vm.notification);

const addRecipientButtonDisabled = computed(
  () =>
    recipientsSelected.value !== STUDENTS_IN_GROUP_SELECTED.SELECTED_RECIPIENTS ||
    (isGroupPreselected.value ? false : isEmpty(selected.value)),
);

const filterGroups = computed(() => {
  if (!sortedGroups.value.length) return [];
  return sortedGroups.value.map((group) => {
    if (group.header) {
      return {
        name: group.name,
        label: group.name,
        value: group.name,
        header: true,
      };
    } else {
      return {
        name: group.groupName,
        label: group.groupName,
        value: group.studentGroupCollectionUuid,
        disabled: group.disabled,
        studentGroupCollectionId: group.studentGroupCollectionId,
      };
    }
  });
});

const studentRecipientSelection = computed(() => Object.values(STUDENTS_IN_GROUP_SELECTED));

const studentGroups = computed(() =>
  studentGroupData.value
    ? studentGroupData.value.studentGroup.filter(
        (group) =>
          // can only create/edit notifications as group owner/co-teacher
          group.studentGroupRole === ROLE_IN_GROUP.GROUP_OWNER ||
          group.studentGroupRole === ROLE_IN_GROUP.CO_TEACHER,
      )
    : [],
);

const notificationReceiver = computed(() => {
  const receivers = [];
  const recipients = vm.notification.notificationRecipients ?? [];
  if (props.params?.data?.classGroup) {
    return [
      {
        name: props.params.data.classGroup.groupName,
        subjectKey: props.params.data.classGroup.subjectKey,
      },
    ];
  }
  for (const recipient of recipients) {
    const audienceType = recipient.audienceType;

    if (audienceType === NOTIFICATION_AUDIENCE.INDIVIDUAL) continue;
    // we want group receivers, so do nothing for individual receiver

    const recipientDetails = recipient.groupName.split(" ");
    const [name, subjectKey] = recipientDetails;
    receivers.push({ name, subjectKey });
  }
  return receivers;
});

const notificationIndividualReceiver = computed(() =>
  NotificationUtils.getNotificationIndividualReceiver(vm.notification),
);

const notificationTeacherIndividuals = computed(() =>
  NotificationUtils.getNotificationTeacherIndividuals(vm.notification),
);

const notificationStudentIndividuals = computed(() =>
  NotificationUtils.getNotificationStudentIndividuals(vm.notification),
);

const isClassGroupNotification = computed(
  () => props.params?.data?.scope === NOTIFICATION_SCOPE.GROUP,
);

const hasIndividualReceiver = computed(() => !isEmpty(notificationIndividualReceiver.value));

const hasEditPermission = computed(
  () => !isEmpty(vm.notification) && vm.notification.hasEditPermission,
);

// this function is duplicated in mixin
const isEdit = computed(() => typeof vm.notification !== "undefined" && vm.notification !== null);

//const startDate = computed(() => DateTimeFormatter.formatLocalDateTimeToDateTimeAmPm(vm.notification.startDate));

//const endDate = computed(() => DateTimeFormatter.formatLocalDateTimeToDateTimeAmPm(vm.notification.endDate));

const schoolName = computed(() => vm.notification.notificationRecipients?.[0]?.schoolName);

const audienceType = computed(() => {
  if (isEdit.value) {
    return vm.notification ? [vm.notification.notificationRecipients[0].audienceType] : [];
  }
  return recipientsSelected.value === STUDENTS_IN_GROUP_SELECTED.ALL
    ? [NOTIFICATION_AUDIENCE.ALL]
    : [NOTIFICATION_AUDIENCE.INDIVIDUAL];
});

const showTeacherTags = computed(() => individualTeachers.value.length !== 0);

const showStudentTags = computed(() => individualRecipients.value.length !== 0);

const hasTeacherIndividualNotification = computed(
  () => isClassGroupNotification.value && !isEmpty(notificationTeacherIndividuals.value),
);

const hasStudentIndividualNotification = computed(
  () => isClassGroupNotification.value && !isEmpty(notificationStudentIndividuals.value),
);

// Methods
const validate = () => {
  let isValid = vm.validateBasicFields();

  if (vm.formData.classGroupSelectedUuids.length === 0) {
    vm.error.group = t("announcement_group_empty_message");
  }

  if (
    recipientsSelected.value === STUDENTS_IN_GROUP_SELECTED.SELECTED_RECIPIENTS &&
    isEmpty(individualRecipients.value) &&
    isEmpty(individualTeachers.value)
  ) {
    toast.show("error", {
      title: t("announcement_edit_empty_individual_recipients_error_message"),
    });
    isValid = false;
  }

  for (const key in vm.error) {
    if (vm.error[key] !== "") isValid = false;
  }
  return isValid;
};

const getRequestData = () => {
  const requestData: unknown = {
    title: vm.formData.title,
    rt_content: vm.formData.content,
    startDateTime: vm.formData.startDateTime,
    notify: vm.formData.notify,
    scope: vm.scope,
    audienceTypes: audienceType.value,
  };

  const studentGroupCollectionIds = [];
  let studentGroupIndividualIds = [];
  let studentIndividualIds = [];
  let teacherIndividualIds = [];

  if (isEdit.value) {
    const studentGroupRecipientsMap = vm.formData.studentGroupCollectionIds;
    const groupId = Object.keys(studentGroupRecipientsMap)[0];
    studentGroupCollectionIds.push(groupId);
    studentGroupIndividualIds = studentGroupRecipientsMap[groupId];

    requestData.uuid = vm.notification.uuid;
    requestData.academicYear = vm.notification.notificationRecipients[0].academicYear;
    requestData.studentGroupIndividualIds = studentGroupIndividualIds;
  } else {
    if (props.params?.data?.isStudentGroupCreation) {
      const groupId = Object.keys(vm.formData.studentGroupCollectionIds)[0];
      studentGroupCollectionIds.push(groupId);
    } else {
      selected.value.forEach((group) => {
        studentGroupCollectionIds.push(group.studentGroupCollectionId);
      });
    }

    studentIndividualIds = individualRecipients.value.map((recipient) => recipient.userId);
    teacherIndividualIds = individualTeachers.value.map((recipient) => recipient.userId);

    requestData.academicYear = isGroupPreselected.value
      ? preselectGroup.value.academicYear
      : studentGroupData.value.academicYear;
  }

  requestData.studentGroupCollectionIds = studentGroupCollectionIds;
  requestData.studentIndividualIds = studentIndividualIds;
  requestData.teacherIndividualIds = teacherIndividualIds;
  return requestData;
};

const handleGroupChange = (uuids: string[]) => {
  if (uuids.length === 0) {
    vm.formData.classGroupSelectedUuids = [];
    recipientsSelected.value = studentRecipientSelection.value[0];
    individualRecipients.value = [];
    selected.value = [];
    return;
  }
  vm.error.group = "";
  setDirty(dirtyId.value);
  vm.formData.classGroupSelectedUuids = [];
  selected.value = [];
  uuids.forEach((uuid) => {
    const [targetedStudentGroup] = studentGroups.value.filter(
      (studentGroup) => studentGroup.studentGroupCollectionUuid === uuid,
    );
    if (!isEmpty(targetedStudentGroup)) {
      vm.formData.classGroupSelectedUuids.push(targetedStudentGroup.studentGroupCollectionUuid);
    }
    selected.value.push({
      groupName: targetedStudentGroup.groupName,
      uuid: targetedStudentGroup.studentGroupCollectionUuid,
      studentGroupCollectionId: targetedStudentGroup.studentGroupCollectionId,
    });
  });
};

const handleChangeRecipientsSelected = (value: string) => {
  const prevRecipientsSelected = recipientsSelected.value;
  if (prevRecipientsSelected === value) return;
  recipientsSelected.value = value;

  if (
    value === STUDENTS_IN_GROUP_SELECTED.ALL &&
    (!isEmpty(individualRecipients.value) || !isEmpty(individualTeachers.value))
  ) {
    modal.showMessage("info", {
      title: t("announcement_change_recipient_type_warning_prompt_title"),
      content: t("announcement_change_recipient_type_warning_prompt_content"),
      primaryBtnLabel: t("button_ok"),
      secondaryBtnLabel: t("button_cancel"),
      actionPrimary: () => {
        individualRecipients.value = [];
        vm.formData.studentGroupCollectionIds = {};
        sortedGroups.value.forEach((classGroup) => {
          if (classGroup.disabled) classGroup.disabled = false;
        });

        const closeButton = vm.el.getElementsByClassName("bx--tag__close-icon")[0];
        if (closeButton) {
          closeButton.removeAttribute("disabled");
        }
        individualTeachers.value = [];
        recipientsSelected.value = value;

        setDirty(dirtyId.value);

        const classGroups = Object.keys(vm.formData.studentGroupCollectionIds);
        classGroups.forEach((studentGroupCollectionId) => {
          vm.formData.studentGroupCollectionIds[studentGroupCollectionId] = {
            teachers: [],
            students: [],
          };
        });
        // no need to update group UI behavior since group preselected
        if (isEmpty(preselectGroup.value)) handleUpdateSortedGroups();
      },
      actionSecondary: () => {
        recipientsSelected.value = prevRecipientsSelected;
      },
      actionHideRequest: () => {
        recipientsSelected.value = prevRecipientsSelected;
      },
    });
  } else {
    recipientsSelected.value = value;
    setDirty(dirtyId.value);
  }
};

const handleAddStudent = () => {
  contentSubpageInstance.show(
    AddRecipientsContentSubpage,
    {
      hasParentSubpage: true,
      baseTitle: [...titles.value],
      filterFlags: {
        group: true,
        school: false,
        levelStream: true,
        hideStudentsFromAnySubgroups: false,
        formClass: true,
      },
      excludedUserIds: [],
      studentsInAllSubgroup: [],
      settings: props.params?.settings,
      selectedStudentGroup: isGroupPreselected.value
        ? [
            {
              groupName: preselectGroup.value.groupName,
              uuid: preselectGroup.value.studentGroupCollectionUuid,
              studentGroupCollectionId: preselectGroup.value.studentGroupCollectionId,
            },
          ]
        : selected.value,
      selectedRecipients: vm.formData.studentGroupCollectionIds,
    },
    {
      recipientAdded: handleAddIndividualRecipients,
    },
  );
};

const handleAddTeacher = () => {
  contentSubpageInstance.show(
    AddTeacherFromGroupContentSubpage,
    {
      hasParentSubpage: true,
      baseTitle: [...titles.value],
      filterFlags: {
        group: true,
        name: false,
        email: false,
        school: true,
        role: true,
      },
      excludedUserIds: [],
      settings: props.params?.settings,
      selectedStudentGroup: isGroupPreselected.value
        ? [
            {
              groupName: preselectGroup.value.groupName,
              uuid: preselectGroup.value.studentGroupCollectionUuid,
              studentGroupCollectionId: preselectGroup.value.studentGroupCollectionId,
            },
          ]
        : selected.value,
      selectedTeachers: vm.formData.studentGroupCollectionIds,
    },
    {
      teachersAdded: handleAddIndividualTeachers,
    },
  );
};

const handleRemoveTeachers = (userId: string) => {
  setDirty(dirtyId.value);

  for (const [classGroup, recipients] of Object.entries(vm.formData.studentGroupCollectionIds)) {
    vm.formData.studentGroupCollectionIds[classGroup].teachers = recipients.teachers.filter(
      (recipient: string) => recipient !== userId,
    );
  }

  individualTeachers.value = individualTeachers.value.filter(
    (teacher) => teacher.userId !== userId,
  );
  if (isEmpty(preselectGroup.value)) handleUpdateSortedGroups();
};

const handleRemoveStudents = (userId: string) => {
  setDirty(dirtyId.value);

  for (const [classGroup, recipients] of Object.entries(vm.formData.studentGroupCollectionIds)) {
    vm.formData.studentGroupCollectionIds[classGroup].students = recipients.students.filter(
      (recipient: string) => recipient !== userId,
    );
  }

  individualRecipients.value = individualRecipients.value.filter(
    (student) => student.userId !== userId,
  );
  if (isEmpty(preselectGroup.value)) handleUpdateSortedGroups();
};

const populateForm = () => {
  vm.populateCommonFields();
  if (isGroupPreselected.value) {
    console.log("preselectGroup", preselectGroup.value);
    vm.formData.classGroupSelectedUuids = [preselectGroup.value.studentGroupCollectionUuid];
    vm.formData.studentGroupCollectionIds = {};
    vm.formData.studentGroupCollectionIds[preselectGroup.value.studentGroupCollectionId] = [];
  } else {
    //TODO: dropdown should use groupCollectionId, studentGroupCollectionIds should be renamed as studentGroupRecipientByCollectionId
    vm.formData.classGroupSelectedUuids = isEdit.value
      ? vm.notification.notificationRecipients.map((group) =>
          group.studentGroupCollectionId.toString(),
        )
      : [];

    vm.formData.studentGroupCollectionIds = {};
    if (isEdit.value) {
      vm.formData.studentGroupCollectionIds[
        vm.notification.notificationRecipients[0].studentGroupCollectionId
      ] = vm.notification.notificationRecipients.map((recipient) => recipient.userId);
    }
  }
};

const handleAddIndividualRecipients = (recipients) => {
  // To only retrieve student info
  individualRecipients.value = individualRecipients.value.concat(Object.values(recipients).flat(2));

  const classGroups = Object.keys(recipients);
  classGroups.forEach((studentGroupCollectionId) => {
    const classGroupId = parseInt(studentGroupCollectionId);
    if (isEmpty(vm.formData.studentGroupCollectionIds[classGroupId])) {
      vm.formData.studentGroupCollectionIds[classGroupId] = { teachers: [], students: [] };
    }
    vm.formData.studentGroupCollectionIds[classGroupId].students = Object.values(recipients)
      .flat(2)
      .filter(
        (recipient) => recipient.studentGroupCollectionId === parseInt(studentGroupCollectionId),
      )
      .map((recipient) => recipient.userId);
  });
  let individualRecipientsLocal = [];
  Object.values(vm.formData.studentGroupCollectionIds).forEach((recipients) => {
    individualRecipientsLocal = individualRecipientsLocal.concat(
      individualRecipients.value.filter((recipient) =>
        recipients.students.includes(recipient.userId),
      ),
    );
  });

  individualRecipients.value = individualRecipientsLocal.filter(
    (recipientA: unknown, index: number) =>
      index ===
      individualRecipientsLocal.findIndex(
        (recipientB: unknown) => recipientA.userId === recipientB.userId,
      ),
  );
  if (isEmpty(preselectGroup.value)) handleUpdateSortedGroups();
};

const handleAddIndividualTeachers = (recipients) => {
  individualTeachers.value = individualTeachers.value.concat(Object.values(recipients).flat(2));

  const classGroups = Object.keys(recipients);

  classGroups.forEach((studentGroupCollectionId) => {
    const classGroupId = parseInt(studentGroupCollectionId);
    if (isEmpty(vm.formData.studentGroupCollectionIds[classGroupId])) {
      vm.formData.studentGroupCollectionIds[classGroupId] = { teachers: [], students: [] };
    }
    vm.formData.studentGroupCollectionIds[classGroupId].teachers = Object.values(recipients)
      .flat(2)
      .filter(
        (recipient) => recipient.studentGroupCollectionId === parseInt(studentGroupCollectionId),
      )
      .map((recipient) => recipient.userId);
  });

  let individualTeachersLocal = [];
  Object.values(vm.formData.studentGroupCollectionIds).forEach((recipients) => {
    individualTeachersLocal = individualTeachersLocal.concat(
      individualTeachers.value.filter((recipient) =>
        recipients.teachers.includes(recipient.userId),
      ),
    );
  });

  individualTeachers.value = individualTeachersLocal.filter(
    (recipientA: unknown, index: number) =>
      index ===
      individualTeachersLocal.findIndex(
        (recipientB: unknown) => recipientA.userId === recipientB.userId,
      ),
  );
  if (isEmpty(preselectGroup.value)) handleUpdateSortedGroups();
};

const handleUpdateSortedGroups = () => {
  const applyDisabled: boolean[] = [];

  sortedGroups.value.forEach((classGroup) => {
    const isHeader = classGroup?.header ?? false;
    classGroup.disabled =
      !isHeader &&
      Object.keys(vm.formData.studentGroupCollectionIds).includes(
        classGroup.studentGroupCollectionId.toString(),
      ) &&
      (!isEmpty(
        vm.formData.studentGroupCollectionIds[classGroup.studentGroupCollectionId].teachers,
      ) ||
        !isEmpty(
          vm.formData.studentGroupCollectionIds[classGroup.studentGroupCollectionId].students,
        ));
    applyDisabled.push(classGroup.disabled);
  });
  const closeButton = vm.$el.getElementsByClassName("bx--tag__close-icon")[0];
  closeButton.removeAttribute("disabled");
  if (applyDisabled.includes(true)) {
    DOMUtils.setAttr(closeButton, "disabled", "true");
  }
};

// Lifecycle
onMounted(() => {
  if (isEdit.value) {
    vm.lockService = new LockService(
      LOCKABLE_RESOURCE_TYPE.NOTIFICATION,
      vm.notification.uuid,
      vm.handleLockRetrievalFail,
    );
    vm.lockService.startLockInterval().then(
      () => {
        populateForm();
        vm.checkAndUpdateStartDateValue();
        sortedGroups.value = NotificationUtils.sortClassesAndGroupsWithHeader(studentGroups.value);
        vm.isLoading = false;
      },
      () => {
        /* Empty Function */
      },
    );
  } else {
    populateForm();
    recipientsSelected.value = studentRecipientSelection.value[0];
    sortedGroups.value = NotificationUtils.sortClassesAndGroupsWithHeader(studentGroups.value);
    vm.isLoading = false;
  }
});

onBeforeUnmount(() => {
  if (vm.lockService) {
    vm.lockService.destroy();
    vm.lockService = null;
  }
});

defineExpose(defaultExpose);

vmExpose({
  id,
  actionHidden,
  setDirty,
  removeDirty,
  setDataState,
  validate,
  getRequestData,
  setErrorMessage,
  titles,
});
</script>
